// @ts-nocheck
import React, { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, AlertTriangle, Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { trpcClient } from "@/lib/trpc";
import {
  mapProductionOrderDetail,
  type ProductionOrderApi,
} from "@/lib/transformers/production-orders";
import { toast } from "sonner";
import type { ProductionOrderFormData } from "../../../shared/schemas/production-order.schema";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import ProductionOrderForm from "./ProductionOrderForm";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";

import { toVnDateString } from "@apv-erp/status";
export default function ProductionOrderEdit() {
  const { orderId } = useParams({ strict: false }) as { orderId: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: order, isLoading, error: detailError, refetch: refetchDetail } = useQuery({
    queryKey: ["production-order", orderId],
    queryFn: async () => {
      const raw = (await trpcClient.productionOrders.getById.query({
        id: orderId,
      })) as ProductionOrderApi;
      return mapProductionOrderDetail(raw);
    },
  });

  const handleSubmit = async (data: ProductionOrderFormData) => {
    setIsSubmitting(true);
    try {
      const targetId = order?.id || orderId;
      // Trạng thái không đổi qua đường sửa chung (máy chủ từ chối): "Lưu & Duyệt Ban Hành" lưu thông tin trước
      // rồi chuyển trạng thái qua updateStatus (kiểm tra quy trình + quyền duyệt).
      const { status: requestedStatus, ...fields } = data as any;
      await trpcClient.productionOrders.update.mutate({
        id: targetId,
        data: fields,
      });

      if (data.printLayouts && data.printLayouts.length > 0) {
        await trpcClient.productionOrders.savePrintLayouts.mutate({
          productionOrderId: targetId,
          layouts: data.printLayouts,
        });
      }

      if (data.materialPlans && data.materialPlans.length > 0) {
        await trpcClient.productionOrders.saveMaterialPlans.mutate({
          productionOrderId: targetId,
          plans: data.materialPlans,
        });
      }

      if (requestedStatus && order?.status && requestedStatus !== order.status) {
        await trpcClient.productionOrders.updateStatus.mutate({
          id: targetId,
          status: requestedStatus,
        });
      }

      toast.success("Cập nhật Lệnh SX thành công");
      await queryClient.invalidateQueries({ queryKey: ["production-orders"] });
      await queryClient.invalidateQueries({
        queryKey: ["production-order"],
      });
      navigate({ to: "/production-orders/$orderId", params: { orderId } });
    } catch (error: any) {
      toast.error(error?.message || "Cập nhật Lệnh SX thất bại");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Gõ/dán URL có id sai (404) hoặc không tải được: không hiển thị khung tải vô hạn
  if (!isLoading && !order) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Lệnh sản xuất", to: "/production-orders" }]}
        entityLabel="Lệnh sản xuất"
        backTo="/production-orders"
        codeOrId={orderId}
        error={detailError}
        onRetry={() => refetchDetail()}
      />
    );
  }
  if (isLoading || !order) {
    return (
      <div className="w-full space-y-4 p-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <StandardDetailLayout
      title="Cập nhật Lệnh Sản Xuất"
      breadcrumbs={[{ label: "Lệnh sản xuất", to: "/production-orders" }]}
      onBack={() =>
        navigate({ to: "/production-orders/$orderId", params: { orderId } })
      }
      showContextPanel={false}
      actions={
        <div className="flex items-center gap-2 shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              navigate({ to: "/production-orders/$orderId", params: { orderId } })
            }
            className="h-8 text-xs min-w-[60px]"
          >
            Hủy
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={isSubmitting}
            className="h-8 text-xs min-w-[85px] bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-xs"
            onClick={() => {
              const submitBtn = document.querySelector('form button[type="submit"]') as HTMLButtonElement;
              if (submitBtn) submitBtn.click();
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Lưu...
              </>
            ) : (
              <>
                <Save className="mr-1 h-3.5 w-3.5" /> Cập nhật
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="w-full space-y-4 px-0 py-4 animate-in fade-in duration-300">
        {(order.status === "IN_PROGRESS" || order.status === "PRODUCED") && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs shadow-2xs">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
            <span>
              <strong>Lưu ý:</strong> Lệnh đang chạy trên chuyền xưởng ({order.status}) — Phối hợp Quản đốc khi điều chỉnh thông số kỹ thuật hoặc quy trình.
            </span>
          </div>
        )}
        <ProductionOrderForm
          orderId={orderId}
          productionOrderDbId={(order as any).id}
          hideStickyHeader={true}
          defaultValues={{
            customer: order.customer,
            customerName: order.customer?.name || (order as any).customerName || "",
            salesOrder: order.salesOrder,
            salesOrderNumber: order.salesOrder?.orderId || (order as any).orderNumber || "",
            product: order.product,
            productName: order.productName ?? order.product?.productName ?? order.product?.name ?? "",
            poReference: order.poReference ?? "",
            salesOrderId: order.salesOrderId ?? "",
            orderDetailId: order.orderDetailId ?? "",
            productId: order.productId ?? "",
            customerId: order.customerId ?? "",
            technicalSpec: order.technicalSpec,
            techSpecCode: order.technicalSpec?.specId || (order as any).techSpecCode || "",
            quantity: (order.qtyOrder && order.qtyOrder > 0) ? order.qtyOrder : (order.qtyPlan && order.qtyPlan > 0) ? order.qtyPlan : 1,
            qtyAllowance: order.qtyAllowance ?? 0,
            qtyPlan: order.qtyPlan ?? 0,
            deadline: order.deadline
              ? toVnDateString(order.deadline)
              : toVnDateString(),
            priority: (order.priority as any) ?? "NORMAL",
            deploymentModel: (order.deploymentModel as any) ?? undefined,
            status: (order.status as any) ?? "DRAFT",
            // Ghi chú lưu trong techSpecs.notes — trước đây đọc order.notes (không có) nên ô trống và lưu đè mất ghi chú
            notes: order.notes ?? order.techSpecs?.notes ?? "",
            techSpecId: order.techSpecId ?? "",
            layoutCode: (order as any).layoutCode ?? "",
            toolings: (order.toolings as any[]) ?? [],
            prepressPcsPerSheet: order.prepressPcsPerSheet ?? null,
            prepressWasteAllowance: order.prepressWasteAllowance ?? null,
            prepressWastePercentage: order.prepressWastePercentage ?? null,
            totalTargetQty: order.totalTargetQty ?? null,
            totalPlanSheets: order.totalPlanSheets ?? null,
            purchasedPaperSize: order.purchasedPaperSize ?? "",
            printingPaperSize: order.printingPaperSize ?? "",
            cutRatio: order.cutRatio ?? null,
            totalMaterialRequired: order.totalMaterialRequired ?? null,
            specification: order.specification ?? "",
            standardQuantity: order.standardQuantity ?? null,
            allowance: order.allowance ?? null,
            actualQuantity: order.actualQuantity ?? null,
            isComposite: order.isComposite ?? false,
            components: (order.components as any) ?? [],
            orderSources: (order.orderSources as any) ?? (order as any).productionOrderSources ?? [],
            printLayouts: (order as any).printLayouts ?? [],
            materialPlans: (order as any).materialPlans ?? [],
            dieCutIds: (order as any).dieCuts?.map((d: any) => d.id) ?? (order as any).dieCutIds ?? [],
            plateIds: (order as any).plates?.map((p: any) => p.id) ?? (order as any).plateIds ?? [],
          }}
          onSubmit={handleSubmit}
          onCancel={() =>
            navigate({ to: "/production-orders/$orderId", params: { orderId } })
          }
          isSubmitting={isSubmitting}
          submitLabel={order?.status === "DRAFT" ? "Lưu" : "Cập nhật"}
        />
      </div>
    </StandardDetailLayout>
  );
}
